import { userBrowserDetails } from "./checkDeviceAndBrowserType";
import { getCacheSettings, setCachePurgeFlag } from "./purgeCache";
import { deregOfServiceWorker } from "./serviceWorkerDeregHelper";

const displayCacheLog: boolean = (process.env.SHOW_CACHE_LOG?.toLowerCase() === 'true') || false;
const enableServiceWorker: boolean = (process.env.ENABLE_SERVICE_CACHE?.toLowerCase() === 'true') || false;
let enablePurgeCache = false;

try {
  enablePurgeCache = localStorage.getItem("cachingL2") === 'true';
} catch (error) {
    logMessage('User is in Cognito - localStorage not accessible', 'red');
}
const enviromentConsole = process.env.mod?.toLowerCase() === 'development' || process.env.mod?.toLowerCase() === 'release';

/**
 * Represents user browser details.
 */
interface UserBrowserDetails {
  inCognito: boolean;
}

/**
 * Logs messages in a consistent format.
 */
function logMessage(message: string, color: string) {
    if (displayCacheLog) {
        console.log(`%c[SW]%c ${message}`, `background:${color}; color:#f3f3f3; font-weight: bold; padding: 0 8px`, '');
    }
}

async function unregisterExistingWorker() {
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
            await registration.unregister();
            logMessage('Service worker unregistered successfully', 'green');
        }
    } catch (error) {
        logMessage(`Error unregistering service worker: ${error}`, 'red');
    }
}

export async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        logMessage('Service workers are not supported', 'red');
        return;
    }

    if (!enableServiceWorker) {
        logMessage('Service worker is disabled by configuration', 'orange');
        await unregisterExistingWorker();
        return;
    }

    try {
        // Check if we're in the middle of a cache purge
        if (window.__cachePurgeInProgress) {
            logMessage('Cache purge in progress, deferring service worker registration', 'orange');
            // Wait for purge to complete before registering, but with a safety timeout
            await new Promise(resolve => {
                // Add a maximum wait time of 5 seconds to prevent deadlock
                const maxWaitTime = 5000; // 5 seconds
                const startTime = Date.now();
                
                const checkPurgeStatus = () => {
                    const elapsed = Date.now() - startTime;
                    if (!window.__cachePurgeInProgress || elapsed > maxWaitTime) {
                        if (elapsed > maxWaitTime) {
                            logMessage('Timeout waiting for cache purge to complete, proceeding anyway', 'red');
                            // Force clear the flag to prevent future deadlocks
                            window.__cachePurgeInProgress = false;
                        }
                        resolve(undefined);
                    } else {
                        setTimeout(checkPurgeStatus, 100);
                    }
                };
                checkPurgeStatus();
            });
        }

        const registration = await navigator.serviceWorker.register('/brand/_assets/js/sw1.js', {
            scope: '/'
        });

        registration.addEventListener('activate', () => {
            logMessage('Service worker activated', 'green');
        });

        // Handle updates
        registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        logMessage('New service worker installed', 'green');
                    }
                });
            }
        });

        // Add message handler for the service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data.type === 'CACHE_CLEARED') {
                logMessage('Cache cleared by service worker', 'green');
                // Notify any waiting purge operations
                if (window.__cachePurgeInProgress) {
                  setCachePurgeFlag(false);
                }
            }
        });

        if (enablePurgeCache) {
            await getCacheSettings();
        }

    } catch (error) {
        logMessage(`Service worker registration failed: ${error}`, 'red');
        // If registration fails, try unregistering existing worker
        await unregisterExistingWorker();
    }
}

// Call this function when you need to force a service worker update
export async function updateServiceWorker() {
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
            await registration.update();
            logMessage('Service worker update initiated', 'green');
        }
    } catch (error) {
        logMessage(`Service worker update failed: ${error}`, 'red');
    }
}

/**
 * Fetches user browser details.
 */
async function getUserBrowserDetails(): Promise<UserBrowserDetails> {
  try {
    return await userBrowserDetails();
  } catch (error) {
    console.error('%c[SW]%c Error fetching user details:', 'background:red; color:#f3f3f3; font-weight: bold; padding: 0 8px', '', error);
    return { inCognito: false }; // Fallback default
  }
}

/**
 * Handles user details and cache settings.
 */
export async function conditionalServiceWorkerInstall(): Promise<void> {
    const data = await getUserBrowserDetails();
    if(enviromentConsole) {
        console.log('%cNon - Prod Build has been loaded', 'background: #f3f3f3; color: red; font-size: 32px; padding: 8px 16px; margin: 8px 0; font-weight: bold;');
        logMessage(`User Details: ${JSON.stringify(data)}`, 'grey');
    }

    if (data.inCognito) {
        logMessage('User is in Cognito', 'red');
        return;
    }

    if (!enablePurgeCache) {
        getCacheSettings();
        return;
    }

    if (enableServiceWorker) {
        await registerServiceWorker();
        getCacheSettings();
    }
    else{
        deregOfServiceWorker().then(() => {
            if (enableServiceWorker)
                console.log('%c[SW]%c Service worker deregistered','background:blue; color:#f3f3f3; font-weight: bold; padding: 0 8px', '');
        }).catch(error => {
            if (enableServiceWorker)
                console.error('%c[SW]%c Error deregistering service worker:', 'background:red; color:#f3f3f3; font-weight: bold; padding: 0 8px', '', error);
        });
    }
}


